import { useCallback } from "react";
import { authHeader } from "../../utils";
import { IRequestValues, IFilterValue, SortOrder } from "../../components/ui/Declarations";

interface UsePagedListExportProps {
    exportUrl?: string;
    csvExportUrl?: string;
    pageSize: number;
    staticFilterValues: Record<string, any>;
    filterValues: IFilterValue[];
    sortBy: string;
    sortOrder: SortOrder;
}

export const usePagedListExport = ({
    exportUrl,
    csvExportUrl,
    pageSize,
    staticFilterValues,
    filterValues,
    sortBy,
    sortOrder
}: UsePagedListExportProps) => {
    const buildExportData = useCallback((): IRequestValues => {
        const filterData: Record<string, any> = {};

        // Add static filter values
        for (const key in staticFilterValues) {
            filterData[key] = staticFilterValues[key];
        }

        // Add dynamic filter values
        filterValues.forEach((item) => {
            filterData[item.filter.member] = item.value;
        });

        return {
            filter: filterData,
            offset: 0,
            count: pageSize,
            sortBy: sortBy,
            sortOrder: sortOrder,
        };
    }, [staticFilterValues, filterValues, pageSize, sortBy, sortOrder]);

    const downloadExport = useCallback(() => {
        const data = buildExportData();
        const requestOptions = { 
            method: "POST", 
            headers: authHeader(), 
            body: JSON.stringify(data) 
        };

        if (exportUrl) {
            fetch(exportUrl, requestOptions)
                .then((response) => {
                    if (response.ok) {
                        const filename = response.headers.get("content-disposition")?.split('"')[1];
                        response.blob().then((blob) => {
                            const href = window.URL.createObjectURL(blob);
                            const link = document.createElement("a");
                            link.href = href;
                            link.download = filename ?? "";
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        });
                    } else {
                        return Promise.reject({ Error: "Something Went Wrong" });
                    }
                })
                .catch((err) => {
                    return Promise.reject({ Error: "Something Went Wrong", err });
                });
        }
    }, [exportUrl, buildExportData]);

    const handleExportCsv = useCallback(() => {
        const data = buildExportData();
        const requestOptions = { 
            method: "POST", 
            headers: authHeader(), 
            body: JSON.stringify(data) 
        };

        if (csvExportUrl) {
            fetch(csvExportUrl, requestOptions)
                .then((response) => {
                    if (response.ok) {
                        const filename = response.headers.get("content-disposition")?.split('"')[1];
                        response.clone().json().then((responseData) => {
                            const csvData = convertToCsv(responseData.results);
                            downloadCsv(csvData, filename || "export.csv");
                        });
                    } else {
                        return Promise.reject({ Error: "Something Went Wrong" });
                    }
                })
                .catch((err) => {
                    return Promise.reject({ Error: "Something Went Wrong", err });
                });
        }
    }, [csvExportUrl, buildExportData]);

    const convertToCsv = useCallback((data: any[]): string => {
        if (!data?.length) return "";

        const keys = Object.keys(data[0]);
        const escapeCell = (val: unknown): string => {
            if (val == null) return "";
            const s = String(val);
            if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
            return s;
        };

        const headers = keys.join(",");
        const rows = data.map((row) => keys.map((k) => escapeCell(row[k])).join(","));
        return [headers, ...rows].join("\n");
    }, []);

    const downloadCsv = useCallback((csvContent: string, fileName: string): void => {
        const blob = new Blob([csvContent], { type: "text/csv" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, []);

    return {
        downloadExport,
        handleExportCsv
    };
};
